import React from 'react'
import { AppBar, Menu, MenuItem, Toolbar } from '@material-ui/core'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { useAuthRedirect } from '../../hooks/use-auth-redirect'
import { selectCheckedAuth, selectUser } from '../../store/auth/selectors'
import { AppHeaderNotification } from '../app-header-notifications'
import { Loader } from '../loader'
import { AppHeaderDrawer } from './app-header-drawer'
import { AppHeaderLogo } from './app-header-logo'
import { AppHeaderRightMenu } from './app-header-right-menu'
import { AppHeaderSearch } from './app-header-search'
import { useAppHeaderStyles } from './app-header.styles'
import {
  useMenuItems,
  MenuItemWithLabel,
  MenuLinkItemWithLabel,
} from './app-header.hook'
import { BundleNotificationsDrawer } from '../../pages/notifications-page/bundle-notifications-drawer'

type AppHeaderProps = {
  /** White header logos on mobile (primary bar), e.g. main page */
  logoVariant?: 'default' | 'onPrimary'
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  logoVariant = 'default',
}): React.ReactElement => {
  const classes = useAppHeaderStyles()
  const isLoading = !useSelector(selectCheckedAuth)

  const appBarClassName =
    process.env.NODE_ENV === 'test'
      ? classes.appBarStagingEnvironment
      : classes.appBar

  const loaderClassName = isLoading
    ? classes.loaderHolder
    : `${classes.loaderHolder} ${classes.loaderHolderLoaded}`

  const menuWrapperClassName = isLoading
    ? classes.menuWrapper
    : `${classes.menuWrapper} ${classes.menuWrapperLoaded}`

  return (
    <div className={classes.appBarHolder}>
      <AppBar position="static" className={appBarClassName}>
        <Toolbar style={{ overflow: 'hidden' }}>
          <div className={loaderClassName}>
            <Loader size={33} />
          </div>
          <div className={menuWrapperClassName}>
            <AppHeaderMenu logoVariant={logoVariant} />
          </div>
        </Toolbar>
        <BundleNotificationsDrawer />
      </AppBar>
    </div>
  )
}

type AppHeaderMenuProps = {
  logoVariant?: 'default' | 'onPrimary'
}

/**
 * App Header Menu
 * @returns {React.ReactElement}
 */
const AppHeaderMenu: React.FC<AppHeaderMenuProps> = ({
  logoVariant = 'default',
}): React.ReactElement => {
  const classes = useAppHeaderStyles()
  const { authRedirectHandle } = useAuthRedirect()
  const { t } = useTranslation()
  const user = useSelector(selectUser)

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const menuItems = useMenuItems(t, user, authRedirectHandle)

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const renderMainMenuItem = (item: MenuLinkItemWithLabel) => {
    const commonProps = {
      className: classes.menuItem,
      onClick: item.onClick,
    }

    if (item.isExternal) {
      return (
        <a
          key={item.to}
          href={item.to}
          target="_blank"
          rel="noreferrer"
          {...commonProps}
        >
          {item.label}
        </a>
      )
    }

    return (
      <Link key={item.to} to={item.to} {...commonProps}>
        {item.label}
      </Link>
    )
  }

  const renderConditionalMenuItem = (item: MenuLinkItemWithLabel) => {
    const commonProps = {
      className: classes.menuItem,
    }

    return item.isExternal ? (
      <a // use for external links to ensure they open in a new tab and are not affected by React Router
        key={item.to}
        href={item.to}
        target="_blank"
        rel="noreferrer"
        {...commonProps}
      >
        {item.label}
      </a>
    ) : (
      <Link key={item.to} to={item.to} {...commonProps}>
        {item.label}
      </Link>
    )
  }

  const renderHelpMenuItem = (item: MenuLinkItemWithLabel) => {
    return (
      <a
        key={item.to}
        href={item.to}
        target="_blank"
        rel="noreferrer"
        className={classes.menuItem}
      >
        {item.label}
      </a>
    )
  }

  const renderDropdownMenuItem = (item: MenuItemWithLabel) => (
    <MenuItem key={item.href} onClick={handleMenuClose}>
      <a
        href={item.href}
        className={classes.menuItem}
        target={item.isExternal ? '_blank' : undefined}
        rel={item.isExternal ? 'noreferrer' : undefined}
      >
        {item.label}
      </a>
    </MenuItem>
  )

  return (
    <div className={classes.menuBlockWrapper}>
      <AppHeaderLogo variant={logoVariant} />
      <div className={classes.menuBlockLeft}>
        {menuItems.mainItems.map(renderMainMenuItem)}
        {renderConditionalMenuItem(menuItems.conditionalItem)}
        {renderHelpMenuItem(menuItems.helpItem)}
        <div className={classes.menuItem}>
          <span
            aria-controls="simple-menu"
            aria-haspopup="true"
            onClick={handleMenuClick}
            className={classes.dropdownMenuIcon}
          >
            <MoreHorizIcon />
          </span>
          <Menu
            id="simple-menu"
            className={classes.dropdownMenu}
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {menuItems.dropdownItems.map(renderDropdownMenuItem)}
          </Menu>
        </div>
      </div>
      <div className={classes.menuBlockRight}>
        <AppHeaderSearch />
        {!!user.userId && <AppHeaderNotification />}
        <AppHeaderRightMenu />
        <AppHeaderDrawer logoVariant={logoVariant} />
      </div>
    </div>
  )
}
